import React, { useState, useRef, useEffect } from 'react';
import styles from './LoanMod1.module.css';
import { X, Check, Upload } from 'lucide-react';
import api, { withCompanyId } from "../../../Components/API/Api";

const LoanMod1 = ({ isOpen, onClose }) => {
  const [step, setStep] = useState('intro');
  const [selectedFile, setSelectedFile] = useState(null);
  const [loanProduct, setLoanProduct] = useState(''); // maps to loan_id
  const [bankAccount, setBankAccount] = useState(''); // maps to bank_id
    const [saving, setSaving] = useState(false);
  // State for dynamically fetched loan products and bank accounts
  const [loanProducts, setLoanProducts] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  
  const [fileError, setFileError] = useState('');
  const [productError, setProductError] = useState('');
  const [bankError, setBankError] = useState('');

  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  // Reset state on modal open
  useEffect(() => {
    if (isOpen) {
      setStep('intro');
      setSelectedFile(null);
      setLoanProduct('');
      setBankAccount('');
      setFileError('');
      setProductError('');
      setBankError('');
      setLoanProducts([]);
      setBankAccounts([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [isOpen]);

  // Fetch loan products AND bank accounts when entering the Form Step
  useEffect(() => {
    const fetchData = async () => {
      if (step === 'form' && isOpen) {
        try {
          const companyId = localStorage.getItem('company_id') || '1';
          
          // Fetch loan products
          const loanRes = await api.get(`/v1/loan_account?company_id=${companyId}`);
          const products = Array.isArray(loanRes.data) ? loanRes.data : loanRes.data?.data || [];
          setLoanProducts(products);

          // Fetch bank accounts – correct endpoint: /v1/bank-accounts
          const bankRes = await api.get(`/v1/bank-accounts?company_id=${companyId}`);
          const accounts = Array.isArray(bankRes.data) ? bankRes.data : bankRes.data?.data || [];
          setBankAccounts(accounts);
        } catch (error) {
          console.error('Failed to fetch data:', error);
          setLoanProducts([]);
          setBankAccounts([]);
        }
      }
    };
    fetchData();
  }, [step, isOpen]);

  if (!isOpen) return null;

  // ---- Validation helpers ----
  const validateFile = (file) => {
    if (!file) return 'Please select a file.';
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ];
    const isValidType = validTypes.includes(file.type) || file.name.endsWith('.xlsx') || file.name.endsWith('.xls');
    if (!isValidType) return 'Only Excel files (.xlsx, .xls) are allowed.';
    const maxSize = 2 * 1024 * 1024; // 2MB
    if (file.size > maxSize) return 'File must be 2MB or smaller.';
    return '';
  };

  const isFormValid = () => {
    const fileErr = validateFile(selectedFile);
    const productErr = !loanProduct ? 'Please select a Loan Product.' : '';
    const bankErr = !bankAccount ? 'Please select a Bank Account.' : '';
    setFileError(fileErr);
    setProductError(productErr);
    setBankError(bankErr);
    return !fileErr && !productErr && !bankErr;
  };

  // ---- General Handlers ----
  const handleUploadDocumentClick = () => {
    setStep('form');
  };

  // ---- File Upload Handlers ----
  const handleFileSelectClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setFileError(validateFile(file));
    } else {
      setSelectedFile(null);
      setFileError('Please select a file.');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      setSelectedFile(file);
      setFileError(validateFile(file));
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add(styles.dragActive);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove(styles.dragActive);
  };

  // ---- API: Download Template ----
  const handleDownloadTemplate = async () => {
    const companyId = localStorage.getItem('company_id') || '1';

    try {
      const res = await api.get(
        `/v1/download-member-loan-import?company_id=${companyId}`,
        { responseType: 'blob' }
      );

      if (res.data.type === 'application/json') {
        const text = await res.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.message || 'Server returned an error.');
      }

      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'loans_data_migration.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      alert('Download failed: ' + (error.response?.data?.message || error.message));
    }
  };



  // ---- API: Save ----
  const handleSave = async () => {
    if (!isFormValid()) return;

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('loan_id', loanProduct);   // correct field name
    formData.append('bank_id', bankAccount);

    const payload = withCompanyId(formData);
    setSaving(true);

    try {
      const response = await api.post(
        '/v1/member/import-loan-data-migration',
        payload
      );

      if (response.status === 200 || response.status === 201) {
        console.log('Save successful:', response.data);
        alert('Data saved successfully!');
        onClose();
      } else {
        throw new Error(`Unexpected status: ${response.status}`);
      }
    } catch (error) {
      console.error('Save failed:', error);
      const msg = error.response?.data?.message || error.message;
      alert('Failed to save data. ' + msg);
    } finally {
      setSaving(false);
    }
  };

  // ---- STEP 1: Introduction UI ----
  const renderIntroStep = () => (
    <>
      <div className={styles.modalHeader}>
        <div>
          <h2 className={styles.modalTitle}>Loans Data Migration</h2>
          <p className={styles.subtitle}>Three steps to migrate your loans data</p>
        </div>
        <button className={styles.closeBtn} onClick={onClose}>
          <X size={24} color="#9ca3af" />
        </button>
      </div>

      <div className={styles.dashedBox}>
        <div className={styles.iconWrapper}>
          <Upload size={28} color="#16a34a" />
        </div>
        <p className={styles.fileTitle}>loans_data_migration.xlsx</p>
        <p className={styles.fileDesc}>
          Download our ready-made template, fill in your data offline at your own pace, then re-upload when you're done
        </p>
      </div>

      <div className={styles.stepsContainer}>
        <div className={styles.step}>
          <div className={styles.stepIcon}><Check size={14} color="white" strokeWidth={3} /></div>
          <p className={styles.stepText}>Click Download Template — your members will already be listed in the spreadsheet</p>
        </div>
        <div className={styles.step}>
          <div className={styles.stepIcon}><Check size={14} color="white" strokeWidth={3} /></div>
          <p className={styles.stepText}>Open the file and fill in the outstanding balance for each member</p>
        </div>
        <div className={styles.step}>
          <div className={styles.stepIcon}><Check size={14} color="white" strokeWidth={3} /></div>
          <p className={styles.stepText}>Click on Upload document and upload the completed file to migrate your loans data at once</p>
        </div>
      </div>

      <div className={styles.footer}>
        <button className={styles.btnOutline} onClick={handleUploadDocumentClick}>
          <Upload size={18} />
          Upload Document
        </button>
        <button className={styles.btnSolid} onClick={handleDownloadTemplate}>
          <Upload size={18} />
          Download Template
        </button>
      </div>
    </>
  );

  // ---- STEP 2: Form UI (Vertical Layout with Dynamic Dropdowns) ----
  const renderFormStep = () => (
    <div className={styles.formContainer}>
      <div className={styles.formHeader}>
        <div>
          <h2 className={styles.formTitle}>Loans Data Migration</h2>
          <p className={styles.subtitle}>Three steps to migrate your loans data</p>
        </div>
        <button className={styles.closeBtn} onClick={onClose}>
          <X size={24} color="#000" />
        </button>
      </div>

      <div className={styles.statusBar}>
        <div className={styles.stepIcon}><Check size={14} color="white" strokeWidth={3} /></div>
        <p className={styles.statusText}>Template downloaded - now upload your completed file below</p>
      </div>

      <div
        className={styles.uploadArea}
        ref={dropZoneRef}
        onClick={handleFileSelectClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <div className={styles.uploadIconWrapper}>
          <Upload size={28} color="#10b981" />
        </div>
        <p className={styles.uploadTitle}>
          {selectedFile ? selectedFile.name : 'Click to upload your completed file'}
        </p>
        <p className={styles.uploadSubtitle}>
          or drag and drop .xlsx, .xls accepted
        </p>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: 'none' }}
          accept=".xlsx,.xls"
        />
      </div>
      {fileError && <p className={styles.errorText}>{fileError}</p>}

      {/* Vertical Layout with both dropdowns */}
      <div className={styles.formColumn}>
        <div className={styles.formGroup}>
          <label className={styles.formLabel}>Loan Product</label>
          <select
            className={styles.selectInput}
            value={loanProduct}
            onChange={(e) => {
              setLoanProduct(e.target.value);
              setProductError(e.target.value ? '' : 'Please select a Loan Product.');
            }}
            onBlur={() => setProductError(loanProduct ? '' : 'Please select a Loan Product.')}
          >
            <option value="">Select Product</option>
            {loanProducts.map((product) => (
              <option key={product.id} value={product.id}>
                {product.loan_name}
              </option>
            ))}
          </select>
          {productError && <p className={styles.errorText}>{productError}</p>}
        </div>

        <div className={styles.formGroup}>
          <label className={styles.formLabel}>Bank Account</label>
          <select
            className={styles.selectInput}
            value={bankAccount}
            onChange={(e) => {
              setBankAccount(e.target.value);
              setBankError(e.target.value ? '' : 'Please select a Bank Account.');
            }}
            onBlur={() => setBankError(bankAccount ? '' : 'Please select a Bank Account.')}
          >
            <option value="">Select Bank Account</option>
            {bankAccounts.map((account) => (
              <option key={account.id} value={account.id}>
                {account.gl_name || 'Unnamed Account'}
              </option>
            ))}
          </select>
          {bankError && <p className={styles.errorText}>{bankError}</p>}
        </div>
      </div>

      <div className={styles.formFooter}>
        <button className={styles.btnCancel} onClick={onClose}>Cancel</button>
        <button
          className={styles.btnImport}
          onClick={handleSave}
          disabled={!selectedFile || !loanProduct || !bankAccount || saving}
        >
          {saving ? 'saving.....' : 'Save'}
        </button>
      </div>
    </div>
  );

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div
        className={`${styles.modalContent} ${step === 'form' ? styles.wideModal : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {step === 'intro' ? renderIntroStep() : renderFormStep()}
      </div>
    </div>
  );
};

export default LoanMod1;